import PropTypes from 'prop-types'
import React, { useRef, useState, useEffect, useLayoutEffect } from 'react'
import styles from './TabSelector.module.scss'

const propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired, // Can be string, FormattedMessage, or any React node
    }),
  ).isRequired,
  activeTab: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired,
  className: PropTypes.string,
}

function TabSelector(props) {
  const { tabs, activeTab, onTabChange, className = '' } = props ?? {}
  const scrollContainerRef = useRef(null)
  const [showLeftArrow, setShowLeftArrow] = useState(false)
  const [showRightArrow, setShowRightArrow] = useState(false)
  const [selectedOffsetLeft, setSelectedOffsetLeft] = useState(0)
  const [selectedWidth, setSelectedWidth] = useState(0)
  const [selectedHeight, setSelectedHeight] = useState(0)

  const checkOverflow = () => {
    const container = scrollContainerRef.current
    if (!container) return

    const hasOverflow = container.scrollWidth > container.clientWidth + 25
    const isAtStart = container.scrollLeft <= 0
    const isAtEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth - 2

    setShowLeftArrow(hasOverflow && !isAtStart)
    setShowRightArrow(hasOverflow && !isAtEnd)
  }

  // Measure the selected button for the background animation
  useLayoutEffect(() => {
    const measureSelectedButton = () => {
      const container = scrollContainerRef.current
      if (!container) return

      const selectedButton = container.querySelector(`[data-tab="${activeTab}"]`)
      if (selectedButton) {
        const { offsetLeft } = selectedButton
        const width = selectedButton.clientWidth
        const height = selectedButton.clientHeight

        setSelectedOffsetLeft(offsetLeft)
        setSelectedWidth(width)
        setSelectedHeight(height)
      }
    }

    measureSelectedButton()
    const timer = setTimeout(measureSelectedButton, 10)
    return () => clearTimeout(timer)
  }, [activeTab])

  useEffect(() => {
    checkOverflow()
    window.addEventListener('resize', checkOverflow)
    return () => window.removeEventListener('resize', checkOverflow)
  }, [tabs])

  const scroll = (direction) => {
    const container = scrollContainerRef.current
    if (!container) return

    const scrollAmount = 200
    const newScrollLeft =
      direction === 'left' ? container.scrollLeft - scrollAmount : container.scrollLeft + scrollAmount

    container.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth',
    })

    // Update arrow visibility after scroll
    setTimeout(checkOverflow, 300)
  }

  return (
    <div className={`${styles.wrapper} ${className}`}>
      {showLeftArrow && (
        <button type="button" className={styles.arrowButton} onClick={() => scroll('left')} aria-label="Scroll left">
          <i className="fas fa-chevron-left" />
        </button>
      )}

      <div ref={scrollContainerRef} className={styles.container} onScroll={checkOverflow}>
        <div
          className={styles.selectedBackground}
          style={{
            left: selectedOffsetLeft + 5,
            width: selectedWidth - 10,
            height: selectedHeight - 8,
          }}
        />

        <div className={styles.tabsWrapper}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              data-tab={tab.key}
              className={`text-bold ${styles.tab}`}
              onClick={() => onTabChange(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {showRightArrow && (
        <button type="button" className={styles.arrowButton} onClick={() => scroll('right')} aria-label="Scroll right">
          <i className="fas fa-chevron-right" />
        </button>
      )}
    </div>
  )
}

TabSelector.propTypes = propTypes

export default TabSelector
